import { useEffect, useRef } from "react";
import { DEFAULT_COL_WIDTH, DEFAULT_ROW_HEIGHT, HEADER_COL_WIDTH, HEADER_ROW_HEIGHT } from "../constants";

/**
 * useKeyboardNav
 * Arrow-key navigation between cells, with auto-scroll-into-view and
 * Shift-extend semantics for the selection rectangle.
 *
 * The current cell is read from a ref and every state setter is called
 * TOP-LEVEL in the event handler (never nested inside another setter's
 * updater). Nested updater side effects ran during React's render phase,
 * which could apply the cursor move and the selection move in different
 * flushes — the visible symptom was TWO highlighted cells after pressing
 * an arrow key (QA #3).
 */
export default function useKeyboardNav({
  activeModel, normalizedRows, maxCols, colWidths, rowHeights,
  gridWrapperRef, selectedCell, setSelectedCell, setSelection, setSelectionType,
}) {
  const cellRef = useRef(selectedCell);
  cellRef.current = selectedCell;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        !["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"].includes(e.key) ||
        e.target.tagName.toLowerCase() === "input" ||
        e.target.tagName.toLowerCase() === "textarea" ||
        !activeModel
      ) return;

      e.preventDefault();

      const prev = cellRef.current || { row: 0, col: 0 };
      let newRow = prev.row;
      let newCol = prev.col;

      if (e.key === "ArrowUp") newRow = Math.max(0, prev.row - 1);
      if (e.key === "ArrowDown") newRow = Math.min(normalizedRows.length - 1, prev.row + 1);
      if (e.key === "ArrowLeft") newCol = Math.max(0, prev.col - 1);
      if (e.key === "ArrowRight") newCol = Math.min(maxCols - 1, prev.col + 1);

      const newCell = { row: newRow, col: newCol };

      if (e.shiftKey) {
        setSelection((prevSel) => ({ ...prevSel, focus: newCell }));
      } else {
        setSelection({ anchor: newCell, focus: newCell, dragging: false });
        // Collapse to a single CELL selection - otherwise a prior row/col
        // header selection leaves selectionType stale and the moved cursor
        // renders as a whole-row/col highlight instead of one cell.
        if (typeof setSelectionType === "function") setSelectionType("cell");
      }
      setSelectedCell(newCell);

      // Auto-scroll into view after state updates.
      setTimeout(() => {
        const el = gridWrapperRef.current;
        if (!el) return;
        let targetLeft = 0;
        for (let i = 0; i < newCol; i++) targetLeft += colWidths[i] || DEFAULT_COL_WIDTH;
        let targetTop = 0;
        for (let i = 0; i < newRow; i++) targetTop += rowHeights[i] || DEFAULT_ROW_HEIGHT;
        const cellWidth = colWidths[newCol] || DEFAULT_COL_WIDTH;
        const cellHeight = rowHeights[newRow] || DEFAULT_ROW_HEIGHT;
        const { scrollLeft, scrollTop, clientWidth, clientHeight } = el;

        if (targetLeft + cellWidth + HEADER_COL_WIDTH > scrollLeft + clientWidth) {
          el.scrollLeft = targetLeft + cellWidth + HEADER_COL_WIDTH - clientWidth + 20;
        } else if (targetLeft < scrollLeft) {
          el.scrollLeft = Math.max(0, targetLeft - 20);
        }
        if (targetTop + cellHeight + HEADER_ROW_HEIGHT > scrollTop + clientHeight) {
          el.scrollTop = targetTop + cellHeight + HEADER_ROW_HEIGHT - clientHeight + 20;
        } else if (targetTop < scrollTop) {
          el.scrollTop = Math.max(0, targetTop - 20);
        }
      }, 10);
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [activeModel, normalizedRows.length, maxCols, colWidths, rowHeights,
      gridWrapperRef, setSelectedCell, setSelection, setSelectionType]);
}
